<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatDate } from '@/lib/date_helper.js'
import { isCompletedStatus, isExpiredStatus } from '@/lib/job_status_helper.js'

const props = defineProps({
  quote: {
    type: Object,
    required: true
  },
  active: {
    type: Boolean
  }
})

// I18n
const { t } = useI18n()

// Stores
const router = useRouter()
const route = useRoute()

// Variables
const isLoading = ref(true)

onMounted(async () => {
})
</script>

<template>
  <div
    class="relative flex flex-col rounded-xl xl:min-w-[400px] w-full min-h-[100px] cursor-pointer transition ease-in-out duration-200 border mb-4 quote-list-item"
    :class="{'active-quote': active, 'has-prices': isCompletedStatus(quote.job_status)}">
    <RouterLink :to="{name: route.name, params: {id: quote.id}}"
                class="w-full quote-header-overlay rounded-t-xl min-h-[100px] ">
      <div class="p-4 w-full">
        <div class="flex flex-col">
          <p v-if="quote.created_at" class="uppercase text-xs">
            {{ t('my_garage.quotes.requested', { date: formatDate(quote.created_at) }) }}
          </p>
          <div class="flex items-start gap-2 pb-1 border-b">
            <div class="flex-shrink-0 mt-1">
              <span v-if="isCompletedStatus(quote.job_status)" class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-100" :title="quote.job_status === 'historical' ? 'Historical' : 'Completed'">
                <svg class="w-2.5 h-2.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
              </span>
              <span v-else-if="isExpiredStatus(quote.job_status)" class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200" :title="quote.job_status === 'stale' ? 'Stale' : 'Expired'">
                <svg class="w-2.5 h-2.5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                </svg>
              </span>
              <span v-else class="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100" title="In Progress">
                <svg class="w-2.5 h-2.5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
            </div>
            <p class="font-semibold text-lg line-clamp-2 my-auto flex-1">
              {{ quote.service_title }}
            </p>
          </div>
          <div class="flex">
            <p v-if="quote.user_vehicle_service_histories && quote.user_vehicle_service_histories[0] && quote.user_vehicle_service_histories[0].status === 'cancelled'" class="text-sm line-clamp-1">
              {{ t('my_garage.quote_card.cancelled') }}
            </p>
            <p v-else class="text-sm line-clamp-1">{{ t('my_garage.quotes.expires', { date: formatDate(quote.expires_at) }) }}</p>
          </div>
        </div>
      </div>
    </RouterLink>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/my_garage/quote';

.quote-list-item {
  background: #f8f9fa;
  position: relative;
  
  &.has-prices {
    border: 2px solid #22c55e;
    background: #f0fdf4;
  }
  
  &.active-quote {
    background: white;
    
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 4px solid #b05b34;
      animation: outline-fill 0.8s ease-out forwards;
    }
    
    &.has-prices {
      background: white;
      
      &::before {
        border-color: #22c55e;
      }
    }
  }
  
  &:hover:not(.active-quote) {
    outline: 2px solid #6c757d;
  }
}

@keyframes outline-fill {
  0% {
    clip-path: polygon(50% 0, 50% 0, 50% 0, 50% 0);
  }
  5% {
    clip-path: polygon(40% 0, 60% 0, 60% 0, 40% 0);
  }
  10% {
    clip-path: polygon(30% 0, 70% 0, 70% 0, 30% 0);
  }
  15% {
    clip-path: polygon(20% 0, 80% 0, 80% 0, 20% 0);
  }
  20% {
    clip-path: polygon(10% 0, 90% 0, 90% 0, 10% 0);
  }
  25% {
    clip-path: polygon(0 0, 100% 0, 100% 0, 0 0);
  }
  30% {
    clip-path: polygon(0 0, 100% 0, 100% 10%, 0 10%);
  }
  35% {
    clip-path: polygon(0 0, 100% 0, 100% 20%, 0 20%);
  }
  40% {
    clip-path: polygon(0 0, 100% 0, 100% 30%, 0 30%);
  }
  45% {
    clip-path: polygon(0 0, 100% 0, 100% 40%, 0 40%);
  }
  50% {
    clip-path: polygon(0 0, 100% 0, 100% 50%, 0 50%);
  }
  55% {
    clip-path: polygon(0 0, 100% 0, 100% 60%, 0 60%);
  }
  60% {
    clip-path: polygon(0 0, 100% 0, 100% 70%, 0 70%);
  }
  65% {
    clip-path: polygon(0 0, 100% 0, 100% 80%, 0 80%);
  }
  70% {
    clip-path: polygon(0 0, 100% 0, 100% 90%, 0 90%);
  }
  75% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  }
  100% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  }
}
</style>
